import { useEffect, useMemo, useState } from 'react'
import { supabase } from "../utils/supabase"
import { getMerchantForOwner } from "../utils/merchantUtils"
import { getCurrencySymbol } from '../utils/currencyUtils'
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Stack, Step, StepLabel, Stepper, TextField, Typography, FormControlLabel, Checkbox, Select, InputLabel, FormControl, Tooltip, useMediaQuery, useTheme, Alert } from '@mui/material'
import { LocalizationProvider, TimePicker, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import DeleteIcon from '@mui/icons-material/Delete'
import dayjs from 'dayjs'

export default function MerchantEditor({ open, onClose, ownerId, merchant, onSaved }) {
	const isEdit = Boolean(merchant?.id)
	const [activeStep, setActiveStep] = useState(0) // 0 details, 1 hours, 2 closed, 3 fee policy
	const theme = useTheme()
	const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

	const [name, setName] = useState(merchant?.name || "")
	const [timezone, setTimezone] = useState(merchant?.timezone || 'America/Los_Angeles')
	const [address, setAddress] = useState(merchant?.address || "")
	const [phone, setPhone] = useState(merchant?.phone || "")
	const [description, setDescription] = useState(merchant?.description || "")
	const [taxRate, setTaxRate] = useState(merchant?.tax_rate || '')

	// Fee policy fields
	const [lateCancelHours, setLateCancelHours] = useState(merchant?.late_cancel_hours || '')
	const [lateCancelHoursCustom, setLateCancelHoursCustom] = useState(false)
	const [lateCancelFeeType, setLateCancelFeeType] = useState(merchant?.late_cancel_fee_type || '')
	const [lateCancelFeeValue, setLateCancelFeeValue] = useState(merchant?.late_cancel_fee_value || '')
	const [lateCancelFeeCustom, setLateCancelFeeCustom] = useState(false)
	const [noShowFeeType, setNoShowFeeType] = useState(merchant?.no_show_fee_type || '')
	const [noShowFeeValue, setNoShowFeeValue] = useState(merchant?.no_show_fee_value || '')
	const [noShowFeeCustom, setNoShowFeeCustom] = useState(false)

	const emptyHours = useMemo(() => ({ "0": [], "1": [], "2": [], "3": [], "4": [], "5": [], "6": [] }), [])
	const [weeklyHours, setWeeklyHours] = useState(merchant?.weekly_hours || emptyHours)
	const [editingDay, setEditingDay] = useState("1")
	const [tempStartStr, setTempStartStr] = useState("")
	const [tempEndStr, setTempEndStr] = useState("")
	const [startOpen, setStartOpen] = useState(false)
	const [endOpen, setEndOpen] = useState(false)

	const [closedDates, setClosedDates] = useState(merchant?.closed_recurring_dates || [])
	const [tempClosedDate, setTempClosedDate] = useState(null)
	const [dateOpen, setDateOpen] = useState(false)
	const [saving, setSaving] = useState(false)
	const [errorMessage, setErrorMessage] = useState("")

	const timeOptions = useMemo(() => {
		const out = []
		for (let h = 0; h < 24; h++) {
			for (let m = 0; m < 60; m += 15) {
				out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
			}
		}
		return out
	}, [])
	const dayLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

	useEffect(() => {
		if (!open) return
		setActiveStep(0)
		setName(merchant?.name || "")
		setTimezone(merchant?.timezone || 'America/Los_Angeles')
		setAddress(merchant?.address || "")
		setPhone(merchant?.phone || "")
		setDescription(merchant?.description || "")
		setTaxRate(merchant?.tax_rate || '')

		// Initialize fee policy fields
		const hours = merchant?.late_cancel_hours != null ? String(merchant.late_cancel_hours) : ''
		const hoursIsCustom = hours !== '' && hours !== '24' && hours !== '48' && hours !== '72'
		setLateCancelHours(hours)
		setLateCancelHoursCustom(hoursIsCustom)

		setLateCancelFeeType(merchant?.late_cancel_fee_type || '')
		const lateCancelValue = merchant?.late_cancel_fee_value || ''
		const lateCancelIsCustom = lateCancelValue !== '' &&
			(merchant?.late_cancel_fee_type === 'percentage' ?
				(lateCancelValue !== 25 && lateCancelValue !== 50) :
				(lateCancelValue !== 1000 && lateCancelValue !== 2500))
		setLateCancelFeeValue(lateCancelValue)
		setLateCancelFeeCustom(lateCancelIsCustom)

		setNoShowFeeType(merchant?.no_show_fee_type || '')
		const noShowValue = merchant?.no_show_fee_value || ''
		const noShowIsCustom = noShowValue !== '' &&
			(merchant?.no_show_fee_type === 'percentage' ?
				(noShowValue !== 50 && noShowValue !== 100) :
				(noShowValue !== 2500 && noShowValue !== 5000))
		setNoShowFeeValue(noShowValue)
		setNoShowFeeCustom(noShowIsCustom)

		setWeeklyHours(merchant?.weekly_hours || emptyHours)
		setEditingDay("1")
		setTempStartStr("")
		setTempEndStr("")
		setClosedDates(merchant?.closed_recurring_dates || [])
		setTempClosedDate(null)
		setSaving(false)
		setErrorMessage("")
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, merchant?.id])

	const addInterval = () => {
		if (!tempStartStr || !tempEndStr) return
		if (tempEndStr <= tempStartStr) return
		setWeeklyHours((prev) => ({
			...prev,
			[editingDay]: [...(prev[editingDay] || []), { start: tempStartStr, end: tempEndStr }],
		}))
		setTempStartStr("")
		setTempEndStr("")
	}

	const removeInterval = (dayKey, index) => {
		setWeeklyHours((prev) => ({
			...prev,
			[dayKey]: (prev[dayKey] || []).filter((_, i) => i !== index),
		}))
	}

	const isDayOpen = (dayKey) => (weeklyHours[dayKey] || []).length > 0
	const toggleDayOpen = (dayKey) => {
		setWeeklyHours((prev) => {
			const currentlyOpen = (prev[dayKey] || []).length > 0
			if (currentlyOpen) {
				return { ...prev, [dayKey]: [] }
			} else {
				return { ...prev, [dayKey]: [] }
			}
		})
	}

	const copyToWeekdays = () => {
		setWeeklyHours((prev) => {
			const src = prev[editingDay] || []
			const out = { ...prev }
				;['1', '2', '3', '4', '5'].forEach((k) => { out[k] = [...src] })
			return out
		})
	}
	const copyToAllDays = () => {
		setWeeklyHours((prev) => {
			const src = prev[editingDay] || []
			const out = { ...prev }
				;['0', '1', '2', '3', '4', '5', '6'].forEach((k) => { out[k] = [...src] })
			return out
		})
	}

	const addClosedDate = () => {
		if (!tempClosedDate) return
		const mmdd = dayjs(tempClosedDate).format('MM-DD')
		setClosedDates((prev) => (prev.includes(mmdd) ? prev : [...prev, mmdd]))
		setTempClosedDate(null)
	}
	const removeClosedDate = (mmdd) => {
		setClosedDates((prev) => prev.filter((d) => d !== mmdd))
	}
	const addCommonHoliday = (mmdd) => {
		setClosedDates((prev) => (prev.includes(mmdd) ? prev : [...prev, mmdd]))
	}
	const clearClosedDates = () => setClosedDates([])

	const handleSave = async () => {
		setSaving(true)
		setErrorMessage("")
		try {
			let payload = {}

			// Build payload based on current step
			if (activeStep === 0) {
				// Step 0: Details
				payload = {
					name: name.trim(),
					timezone,
					address: address.trim() || null,
					phone: phone.trim() || null,
					description: description.trim() || null,
					tax_rate: taxRate === '' || taxRate === null ? null : Number(taxRate),
				}
			} else if (activeStep === 1) {
				// Step 1: Hours
				payload = {
					weekly_hours: weeklyHours,
				}
			} else if (activeStep === 2) {
				// Step 2: Closed dates
				payload = {
					closed_recurring_dates: closedDates,
				}
			} else if (activeStep === 3) {
				// Step 3: Fee policy
				payload = {
					late_cancel_hours: lateCancelHours === '' || lateCancelHours === null ? null : Number(lateCancelHours),
					late_cancel_fee_type: lateCancelFeeType === '' ? null : lateCancelFeeType,
					late_cancel_fee_value: lateCancelFeeValue === '' || lateCancelFeeValue === null ? null : Number(lateCancelFeeValue),
					no_show_fee_type: noShowFeeType === '' ? null : noShowFeeType,
					no_show_fee_value: noShowFeeValue === '' || noShowFeeValue === null ? null : Number(noShowFeeValue),
				}
			}

			let data, error
			if (isEdit) {
				// Update only the fields for the current step
				({ data, error } = await supabase
					.from('merchant')
					.update(payload)
					.eq('id', merchant.id)
					.select('id,name,timezone,address,phone,description,tax_rate,weekly_hours,closed_recurring_dates,late_cancel_hours,late_cancel_fee_type,late_cancel_fee_value,no_show_fee_type,no_show_fee_value')
					.single())
			} else {
				// Guard: if this owner already has a merchant (e.g. the dashboard
				// failed to load it), update that row instead of inserting a
				// duplicate. Resolves to the same row the rest of the app uses.
				const existing = await getMerchantForOwner(ownerId)
				if (existing?.id) {
					({ data, error } = await supabase
						.from('merchant')
						.update(payload)
						.eq('id', existing.id)
						.select('id,name,timezone,address,phone,description,tax_rate,weekly_hours,closed_recurring_dates,late_cancel_hours,late_cancel_fee_type,late_cancel_fee_value,no_show_fee_type,no_show_fee_value')
						.single())
					if (error) throw error
					onSaved && onSaved(data)
					onClose && onClose()
					return
				}
				// For new merchant, include all required fields
				const fullPayload = {
					owner_id: ownerId,
					name: name.trim(),
					timezone,
					address: address.trim() || null,
					phone: phone.trim() || null,
					description: description.trim() || null,
					tax_rate: taxRate === '' || taxRate === null ? null : Number(taxRate),
					late_cancel_hours: lateCancelHours === '' || lateCancelHours === null ? null : Number(lateCancelHours),
					late_cancel_fee_type: lateCancelFeeType === '' ? null : lateCancelFeeType,
					late_cancel_fee_value: lateCancelFeeValue === '' || lateCancelFeeValue === null ? null : Number(lateCancelFeeValue),
					no_show_fee_type: noShowFeeType === '' ? null : noShowFeeType,
					no_show_fee_value: noShowFeeValue === '' || noShowFeeValue === null ? null : Number(noShowFeeValue),
					weekly_hours: weeklyHours,
					closed_recurring_dates: closedDates,
					active: true,
				};
				({ data, error } = await supabase
					.from('merchant')
					.insert(fullPayload)
						.select('id,name,timezone,address,phone,description,tax_rate,weekly_hours,closed_recurring_dates,late_cancel_hours,late_cancel_fee_type,late_cancel_fee_value,no_show_fee_type,no_show_fee_value')
						.single())
			}
			if (error) throw error
			onSaved && onSaved(data)
			onClose && onClose()
		} catch (e) {
			setErrorMessage(e.message || 'Failed to save merchant')
		} finally {
			setSaving(false)
		}
	}

	const startValue = tempStartStr ? dayjs(`1970-01-01T${tempStartStr}`) : null
	const endValue = tempEndStr ? dayjs(`1970-01-01T${tempEndStr}`) : null

	return (
		<Dialog open={open} onClose={onClose} fullScreen={isSmall} fullWidth maxWidth="md">
			<DialogTitle>{isEdit ? 'Edit merchant' : 'Create merchant'}</DialogTitle>
			<DialogContent>
				<Stepper activeStep={activeStep} orientation={isSmall ? 'vertical' : 'horizontal'} alternativeLabel={!isSmall} sx={{ mb: 2 }}>
					<Step><StepLabel>Details</StepLabel></Step>
					<Step><StepLabel>Hours</StepLabel></Step>
					<Step><StepLabel>Closed dates</StepLabel></Step>
					<Step><StepLabel>Fee policy</StepLabel></Step>
				</Stepper>

				{activeStep === 0 && (
					<Stack spacing={2} sx={{ mt: 1 }}>
						<TextField label="Store name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
						<TextField label="Timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} select fullWidth>
							<MenuItem value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</MenuItem>
							<MenuItem value="America/Vancouver">America/Vancouver</MenuItem>
							<MenuItem value="America/Denver">America/Denver</MenuItem>
							<MenuItem value="America/Chicago">America/Chicago</MenuItem>
							<MenuItem value="America/New_York">America/New_York</MenuItem>
						</TextField>
						<TextField label="Address" value={address} onChange={(e) => setAddress(e.target.value)} fullWidth />
						<TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth />
						<TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />
						<TextField
							label="Tax Rate (%)"
							type="number"
							value={taxRate || ''}
							onChange={(e) => setTaxRate(e.target.value === '' ? '' : e.target.value)}
							inputProps={{ min: 0, max: 100, step: 0.01 }}
							helperText="Enter tax percentage (e.g., 8.5 for 8.5%). Leave empty for no tax."
							fullWidth
						/>
					</Stack>
				)}

				{activeStep === 1 && (
					<Stack spacing={2} sx={{ mt: 1 }}>
						<Stack direction={isSmall ? 'column' : 'row'} spacing={2} alignItems={isSmall ? 'stretch' : 'center'}>
							<FormControl sx={{ minWidth: isSmall ? undefined : 200 }} fullWidth={isSmall}>
								<InputLabel id="day-label">Day</InputLabel>
								<Select labelId="day-label" label="Day" value={editingDay} onChange={(e) => setEditingDay(e.target.value)}>
									{dayLabels.map((label, idx) => (
										<MenuItem key={idx} value={String(idx)}>{label}</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControlLabel
								control={<Checkbox checked={isDayOpen(editingDay)} onChange={() => toggleDayOpen(editingDay)} />}
								label="Open this day"
							/>
							<Tooltip title="Copy current day's hours to Mon–Fri">
								<Button size="small" onClick={copyToWeekdays} fullWidth={isSmall}>Copy to weekdays</Button>
							</Tooltip>
							<Tooltip title="Copy current day's hours to all days">
								<Button size="small" onClick={copyToAllDays} fullWidth={isSmall}>Copy to all days</Button>
							</Tooltip>
						</Stack>

						<Stack direction={isSmall ? 'column' : 'row'} spacing={2} alignItems={isSmall ? 'stretch' : 'center'}>
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<TimePicker
									ampm={true}
									minutesStep={5}
									label="Start"
									value={startValue}
									open={startOpen}
									onOpen={() => setStartOpen(true)}
									onClose={() => setStartOpen(false)}
									onChange={(v) => setTempStartStr(v ? v.format('HH:mm') : '')}
									slotProps={{ textField: { fullWidth: isSmall, onClick: () => setStartOpen(true), inputProps: { readOnly: true } } }}
								/>
								<TimePicker
									ampm={true}
									minutesStep={5}
									label="End"
									value={endValue}
									open={endOpen}
									onOpen={() => setEndOpen(true)}
									onClose={() => setEndOpen(false)}
									onChange={(v) => setTempEndStr(v ? v.format('HH:mm') : '')}
									slotProps={{ textField: { fullWidth: isSmall, onClick: () => setEndOpen(true), inputProps: { readOnly: true } } }}
								/>
							</LocalizationProvider>
							<Button variant="outlined" onClick={addInterval} disabled={!isDayOpen(editingDay) || !tempStartStr || !tempEndStr || tempEndStr <= tempStartStr} fullWidth={isSmall}>Add interval</Button>
						</Stack>

						{dayLabels.map((label, idx) => {
							const key = String(idx)
							const intervals = weeklyHours[key] || []
							return (
								<div key={key} style={{ marginBottom: 8 }}>
									<Typography variant="subtitle2">{label} {intervals.length === 0 && <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: 8 }}>(Closed)</span>}</Typography>
									{intervals.map((intv, i) => {
										const startTime = dayjs(`1970-01-01T${intv.start}`).format('h:mm A')
										const endTime = dayjs(`1970-01-01T${intv.end}`).format('h:mm A')
										return (
											<Stack key={i} direction="row" alignItems="center" spacing={1} style={{ marginTop: 4 }}>
												<Chip label={`${startTime} - ${endTime}`} />
												<IconButton size="small" onClick={() => removeInterval(key, i)} aria-label="delete">
													<DeleteIcon fontSize="small" />
												</IconButton>
											</Stack>
										)
									})}
								</div>
							)
						})}
					</Stack>
				)}

				{activeStep === 2 && (
					<Stack spacing={2} sx={{ mt: 1 }}>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker
								views={["month", "day"]}
								label="Add closed date"
								value={tempClosedDate}
								open={dateOpen}
								onOpen={() => setDateOpen(true)}
								onClose={() => setDateOpen(false)}
								onChange={(v) => setTempClosedDate(v)}
								slotProps={{ textField: { fullWidth: isSmall, onClick: () => setDateOpen(true), inputProps: { readOnly: true } } }}
							/>
						</LocalizationProvider>
						<Button variant="outlined" onClick={addClosedDate} disabled={!tempClosedDate} fullWidth={isSmall}>Add</Button>
						<Stack direction="row" spacing={1} flexWrap="wrap">
							{closedDates.length === 0 && (
								<Typography variant="body2" color="text.secondary">No closed dates</Typography>
							)}
							{closedDates.map((d) => (
								<Chip key={d} label={d} onDelete={() => removeClosedDate(d)} />
							))}
						</Stack>
					</Stack>
				)}

				{activeStep === 3 && (
					<Stack spacing={3} sx={{ mt: 1 }}>
						{/* Late Cancellation Policy */}
						<Box>
							<Typography variant="h6" gutterBottom>Cancellation Fee</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
								Set a fee for late cancellations. Leave empty for free cancellation.
							</Typography>

							<Stack spacing={2}>
								{/* Late Cancellation Window */}
								<FormControl fullWidth>
									<InputLabel>Late Cancellation Window</InputLabel>
									<Select
										value={lateCancelHoursCustom ? 'custom' : lateCancelHours}
										label="Late Cancellation Window"
										onChange={(e) => {
											if (e.target.value === 'custom') {
												setLateCancelHoursCustom(true)
												setLateCancelHours('')
											} else {
												setLateCancelHoursCustom(false)
												setLateCancelHours(e.target.value)
											}
										}}
									>
										<MenuItem value="">None (Free cancellation anytime)</MenuItem>
										<MenuItem value="24">24 hours</MenuItem>
										<MenuItem value="48">48 hours</MenuItem>
										<MenuItem value="72">72 hours</MenuItem>
										<MenuItem value="custom">Custom</MenuItem>
									</Select>
								</FormControl>

								{lateCancelHoursCustom && (
									<TextField
										label="Custom Hours"
										type="number"
										value={lateCancelHours || ''}
										onChange={(e) => {
											const val = e.target.value
											setLateCancelHours(val === '' ? '' : val)
										}}
										inputProps={{ min: 1, max: 120 }}
										helperText="Enter hours (1-120)"
										fullWidth
									/>
								)}

								{lateCancelHours && lateCancelHours !== '' && (
									<>
										{/* Fee Type Selection */}
										<FormControl fullWidth>
											<InputLabel>Fee Type</InputLabel>
											<Select
												value={lateCancelFeeType}
												label="Fee Type"
												onChange={(e) => {
													setLateCancelFeeType(e.target.value)
													setLateCancelFeeValue('')
													setLateCancelFeeCustom(false)
												}}
											>
												<MenuItem value="percentage">% of service price</MenuItem>
												<MenuItem value="fixed">Flat fee</MenuItem>
											</Select>
										</FormControl>

										{/* Fee Value Selection */}
										{lateCancelFeeType === 'percentage' && (
											<>
												<FormControl fullWidth>
													<InputLabel>Percentage</InputLabel>
													<Select
														value={lateCancelFeeCustom ? 'custom' : lateCancelFeeValue}
														label="Percentage"
														onChange={(e) => {
															if (e.target.value === 'custom') {
																setLateCancelFeeCustom(true)
																setLateCancelFeeValue('')
															} else {
																setLateCancelFeeCustom(false)
																setLateCancelFeeValue(e.target.value)
															}
														}}
													>
														<MenuItem value="25">25%</MenuItem>
														<MenuItem value="50">50%</MenuItem>
														<MenuItem value="custom">Custom</MenuItem>
													</Select>
												</FormControl>

												{lateCancelFeeCustom && (
													<TextField
														label="Custom Percentage"
														type="number"
														value={lateCancelFeeValue}
														onChange={(e) => setLateCancelFeeValue(e.target.value)}
														inputProps={{ min: 1, max: 100 }}
														helperText="Enter percentage (1-100)"
														fullWidth
													/>
												)}
											</>
										)}

										{lateCancelFeeType === 'fixed' && (
											<>
												<FormControl fullWidth>
													<InputLabel>Flat Fee</InputLabel>
													<Select
														value={lateCancelFeeCustom ? 'custom' : lateCancelFeeValue}
														label="Flat Fee"
														onChange={(e) => {
															if (e.target.value === 'custom') {
																setLateCancelFeeCustom(true)
																setLateCancelFeeValue('')
															} else {
																setLateCancelFeeCustom(false)
																setLateCancelFeeValue(e.target.value)
															}
														}}
													>
														<MenuItem value="1000">$10</MenuItem>
														<MenuItem value="2500">$25</MenuItem>
														<MenuItem value="custom">Custom</MenuItem>
													</Select>
												</FormControl>

												{lateCancelFeeCustom && (
													<TextField
														label="Custom Amount"
														type="number"
														value={lateCancelFeeValue ? lateCancelFeeValue / 100 : ''}
														onChange={(e) => setLateCancelFeeValue(e.target.value ? Math.round(parseFloat(e.target.value) * 100) : '')}
														inputProps={{ min: 0.01, step: 0.01 }}
														helperText="Enter amount in dollars (e.g., 20.00)"
														fullWidth
													/>
												)}
											</>
										)}
									</>
								)}
							</Stack>
						</Box>

						{/* No-Show Policy */}
						<Box>
							<Typography variant="h6" gutterBottom>No-Show Fee</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
								Set a fee for no-shows. Leave empty to use the same as late cancellation fee.
							</Typography>

							<Stack spacing={2}>
								{/* Fee Type Selection */}
								<FormControl fullWidth>
									<InputLabel>Fee Type</InputLabel>
									<Select
										value={noShowFeeType}
										label="Fee Type"
										onChange={(e) => {
											setNoShowFeeType(e.target.value)
											setNoShowFeeValue('')
											setNoShowFeeCustom(false)
										}}
									>
										<MenuItem value="">Same as late cancellation</MenuItem>
										<MenuItem value="percentage">% of service price</MenuItem>
										<MenuItem value="fixed">Flat fee</MenuItem>
									</Select>
								</FormControl>

								{/* Fee Value Selection */}
								{noShowFeeType === 'percentage' && (
									<>
										<FormControl fullWidth>
											<InputLabel>Percentage</InputLabel>
											<Select
												value={noShowFeeCustom ? 'custom' : noShowFeeValue}
												label="Percentage"
												onChange={(e) => {
													if (e.target.value === 'custom') {
														setNoShowFeeCustom(true)
														setNoShowFeeValue('')
													} else {
														setNoShowFeeCustom(false)
														setNoShowFeeValue(e.target.value)
													}
												}}
											>
												<MenuItem value="50">50%</MenuItem>
												<MenuItem value="100">100%</MenuItem>
												<MenuItem value="custom">Custom</MenuItem>
											</Select>
										</FormControl>

										{noShowFeeCustom && (
											<TextField
												label="Custom Percentage"
												type="number"
												value={noShowFeeValue}
												onChange={(e) => setNoShowFeeValue(e.target.value)}
												inputProps={{ min: 1, max: 100 }}
												helperText="Enter percentage (1-100)"
												fullWidth
											/>
										)}
									</>
								)}

								{noShowFeeType === 'fixed' && (
									<>
										<FormControl fullWidth>
											<InputLabel>Flat Fee</InputLabel>
											<Select
												value={noShowFeeCustom ? 'custom' : noShowFeeValue}
												label="Flat Fee"
												onChange={(e) => {
													if (e.target.value === 'custom') {
														setNoShowFeeCustom(true)
														setNoShowFeeValue('')
													} else {
														setNoShowFeeCustom(false)
														setNoShowFeeValue(e.target.value)
													}
												}}
											>
												<MenuItem value="2500">$25</MenuItem>
												<MenuItem value="5000">$50</MenuItem>
												<MenuItem value="custom">Custom</MenuItem>
											</Select>
										</FormControl>

										{noShowFeeCustom && (
											<TextField
												label="Custom Amount"
												type="number"
												value={noShowFeeValue ? noShowFeeValue / 100 : ''}
												onChange={(e) => setNoShowFeeValue(e.target.value ? Math.round(parseFloat(e.target.value) * 100) : '')}
												inputProps={{ min: 0.01, step: 0.01 }}
												helperText="Enter amount in dollars (e.g., 50.00)"
												fullWidth
											/>
										)}
									</>
								)}
							</Stack>
						</Box>

						{/* Policy Preview */}
						{(lateCancelHours || noShowFeeType) && (() => {
							const cs = getCurrencySymbol(merchant?.currency)
							return (
							<Alert severity="info">
								<Typography variant="subtitle2" gutterBottom>Policy Preview:</Typography>
								{lateCancelHours && lateCancelFeeType && lateCancelFeeValue && (
									<Typography variant="body2">
										• Cancellations within {lateCancelHours} hours: {lateCancelFeeType === 'percentage' ? `${lateCancelFeeValue}% of service price` : `${cs}${(lateCancelFeeValue / 100).toFixed(2)}`}
									</Typography>
								)}
								{noShowFeeType && noShowFeeValue ? (
									<Typography variant="body2">
										• No-show: {noShowFeeType === 'percentage' ? `${noShowFeeValue}% of service price` : `${cs}${(noShowFeeValue / 100).toFixed(2)}`}
									</Typography>
								) : lateCancelHours && lateCancelFeeType && lateCancelFeeValue && (
									<Typography variant="body2">
										• No-show: Same as late cancellation ({lateCancelFeeType === 'percentage' ? `${lateCancelFeeValue}% of service price` : `${cs}${(lateCancelFeeValue / 100).toFixed(2)}`})
									</Typography>
								)}
							</Alert>
							)
						})()}
					</Stack>
				)}

				{errorMessage && (
					<Alert severity="error" sx={{ mt: 2 }}>{errorMessage}</Alert>
				)}
			</DialogContent>
			<DialogActions>
				<Stack direction={isSmall ? 'column-reverse' : 'row'} spacing={2} gap={2} width="100%">
					{activeStep > 0 && (
						<Button onClick={() => setActiveStep((s) => s - 1)} disabled={saving} fullWidth={isSmall} variant="outlined" sx={{ m: '0 !important' }}>Back</Button>
					)}
					{activeStep < 3 && (
						<Button onClick={() => setActiveStep((s) => s + 1)} disabled={saving || (activeStep === 0 && !name.trim())} variant="outlined" fullWidth={isSmall} sx={{ m: '0 !important' }}>
							Next
						</Button>
					)}
					<Button
						onClick={handleSave}
						disabled={saving || (activeStep === 0 && !name.trim()) || (!isEdit && activeStep < 3)}
						variant="contained"
						fullWidth={isSmall}
						sx={{ m: '0 !important' }}
					>
						{isEdit ? (saving ? 'Saving…' : 'Save') : (saving ? 'Creating…' : (activeStep === 3 ? 'Create merchant' : 'Create & Save'))}
					</Button>
					<Button variant='contained' color='secondary' onClick={onClose} disabled={saving} fullWidth={isSmall} sx={{ m: '0 !important' }}>Cancel</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	)
} 